import { DownOutlined } from '@ant-design/icons';
import { Bar, BarConfig } from '@ant-design/plots';
import { useQuery } from '@apollo/client';
import {
  ApplicationCountByEvaluator,
  Challenge,
  ChallengeEvaluatorGraph,
  GET_APPLICATION_COUNT_EVALUATOR,
} from '@frontend/data-access';
import { Button, Card, Dropdown } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const states = ['done', 'pending'];

/* eslint-disable-next-line */
export interface ChallengeDashboardEvaluatingApplicationsProps {
  challenge: Challenge & { __typename?: string };
}

export function ChallengeDashboardEvaluatingApplications({
  challenge,
}: ChallengeDashboardEvaluatingApplicationsProps) {
  const { t } = useTranslation('common');

  const COLOR_MAP = {
    Completed: '#63E063',
    Pending: '#E5E8ED',
  };

  const LABEL_MAP = useMemo(() => {
    return {
      done: t('completed'),
      pending: t('pending'),
    };
  }, [t]);

  const sortOptions = useMemo(
    () => [
      { key: 'HL', label: t('highest_to_lowest') },
      { key: 'LH', label: t('lowest_to_highest') },
    ],
    [t]
  );

  const [data, setData] = useState<ChallengeEvaluatorGraph[]>([]);
  const [sortOption, setSortOption] = useState(sortOptions[0]);

  const { data: rawData, loading } = useQuery<{
    applicationCountByEvaluator: ApplicationCountByEvaluator[];
  }>(GET_APPLICATION_COUNT_EVALUATOR, {
    fetchPolicy: 'no-cache',
    variables: {
      challengeId: challenge.id,
      order: true,
    },
  });

  useEffect(() => {
    if (!rawData?.applicationCountByEvaluator) return;

    const sortedArr = rawData.applicationCountByEvaluator.sort((a, b) => {
      const doneA =
        a.applications.find(({ status }) => status.toLowerCase() === 'done')
          ?.application || 0;
      const doneB =
        b.applications.find(({ status }) => status.toLowerCase() === 'done')
          ?.application || 0;
      return sortOption.key === 'HL' ? doneB - doneA : doneA - doneB;
    });

    const result = sortedArr.flatMap((item) =>
      states.map((state) => ({
        name: item.evaluator?.fullName,
        state: LABEL_MAP[state as keyof typeof LABEL_MAP],
        count:
          item.applications.find(({ status }) => status.toLowerCase() === state)
            ?.application || 0,
      }))
    );
    setData(result as ChallengeEvaluatorGraph[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption, rawData]);

  const config: BarConfig = {
    data: data,
    label: false,
    stack: true,
    loading: loading,
    xField: 'count',
    yField: 'name',
    seriesField: 'state',
    color: ({ state }) => COLOR_MAP[state as keyof typeof COLOR_MAP],
    legend: { position: 'bottom', flipPage: false },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
        labelFormatter: (value: string) => {
          const str = value.toString();
          if (str.length > 30) {
            return str.slice(0, 30) + '...';
          }
          return str;
        },
      },
    },
  };

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-gray-900 font-medium">
              {t('evaluators_by_number_of_evaluating_applications')}
            </div>
            <span className="text-gray-700 font-normal">
              {t('by_number_of_evaluating_applications')}
            </span>
          </div>
          <div>
            <Dropdown
              menu={{
                items: sortOptions.map((item) => ({
                  ...item,
                  onClick: () => setSortOption(item),
                })),
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button shape="round">
                {sortOption.label}
                <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <Bar {...config} />
      </div>
    </Card>
  );
}

export default ChallengeDashboardEvaluatingApplications;
